import React, { useEffect, useMemo, useState } from 'react';
import { RubricActionConfirmationModal } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricList } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricListEmptyState } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricListErrorState } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricListLoadingState } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricListTabs } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricPendingAction } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricToastData } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricToastStack } from 'apps/sigma2-ui/src/components/rubrics/RubricList';
import { RubricListHeaderCards } from 'apps/sigma2-ui/src/components/rubrics/RubricListHeaderCards';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface ListRubricsViewItemProps {
  id: string;
  type: RubricGradingType;
  name: string;
  jobTitles: string[];
  status: RubricStatus;
}

export interface ListRubricsViewProps {
  version: 'loaded' | 'loading' | 'error' | 'empty';
  canManageRubrics: boolean;
  list: ListRubricsViewItemProps[];
  currentTab: 'all' | RubricStatus;
  onTabClick: (tab: 'all' | RubricStatus) => void;
  archivingRubric: string | null;
  unarchivingRubric: string | null;
  deletingRubric: string | null;
  publishingRubric: string | null;
  unpublishingRubric: string | null;
  archiveRubric: (rubricId: string) => void;
  unarchiveRubric: (rubricId: string) => void;
  deleteRubric: (rubricId: string) => void;
  publishRubric: (rubricId: string) => void;
  unpublishRubric: (rubricId: string) => void;
  pendingAction: RubricPendingAction | null;
  setPendingAction: (pendingAction: RubricPendingAction | null) => void;
  toasts: RubricToastData[];
  dismissToast: (toastId: string) => void;
}

const shouldShowRubricInTab = ({
  status,
  currentTab,
}: {
  status: RubricStatus;
  currentTab: 'all' | RubricStatus;
}) => {
  if (currentTab === 'all') {
    return status !== 'archived';
  }

  return status === currentTab;
};

const RubricListLoadedState: React.FC<{
  visibleRubrics: ListRubricsViewItemProps[];
  canManageRubrics: boolean;
  archivingRubric: string | null;
  unarchivingRubric: string | null;
  deletingRubric: string | null;
  publishingRubric: string | null;
  unpublishingRubric: string | null;
  setPendingAction: (pendingAction: RubricPendingAction | null) => void;
  setOpenedDropdown: (openedDropdown: string | null) => void;
  openedDropdown: string | null;
}> = ({
  visibleRubrics,
  canManageRubrics,
  archivingRubric,
  unarchivingRubric,
  deletingRubric,
  publishingRubric,
  unpublishingRubric,
  setPendingAction,
  setOpenedDropdown,
  openedDropdown,
}) => {
  if (visibleRubrics.length === 0) {
    return (
      <RubricListEmptyState
        canCreateRubrics={canManageRubrics}
        interviewRubricHref="/rubrics/create/interview"
        resumeRubricHref="/rubrics/create/resume"
        writtenResponseRubricHref="/rubrics/create/questions"
      />
    );
  }

  return (
    <RubricList
      items={visibleRubrics.map((rubric) => ({
        ...rubric,
        editHref: `/rubrics/edit/${rubric.id}`,
        duplicateHref: `/rubrics/duplicate/${rubric.id}`,
        showActionsMenu: canManageRubrics,
        canArchive: canManageRubrics && rubric.status !== 'archived',
        canUnarchive: canManageRubrics && rubric.status === 'archived',
        canDelete: canManageRubrics && rubric.status === 'archived',
        canDuplicate: canManageRubrics && rubric.status !== 'archived',
        canPublish: canManageRubrics && rubric.status === 'draft',
        canUnpublish: canManageRubrics && rubric.status === 'published',
        canEdit: true, // when you are archiving, the page we take them to is read only and only allows restoring
        archivePending: archivingRubric === rubric.id,
        unarchivePending: unarchivingRubric === rubric.id,
        deletePending: deletingRubric === rubric.id,
        publishPending: publishingRubric === rubric.id,
        unpublishPending: unpublishingRubric === rubric.id,
        archiveFn: () => {
          setPendingAction({
            rubricId: rubric.id,
            action: 'archive',
          });
          setOpenedDropdown(null);
        },
        unarchiveFn: () => {
          setPendingAction({
            rubricId: rubric.id,
            action: 'unarchive',
          });
          setOpenedDropdown(null);
        },
        deleteFn: () => {
          setPendingAction({
            rubricId: rubric.id,
            action: 'delete',
          });
          setOpenedDropdown(null);
        },
        publishFn: () => {
          setPendingAction({
            rubricId: rubric.id,
            action: 'publish',
          });
          setOpenedDropdown(null);
        },
        unpublishFn: () => {
          setPendingAction({
            rubricId: rubric.id,
            action: 'unpublish',
          });
          setOpenedDropdown(null);
        },
        menuOpen: openedDropdown === rubric.id,
        setMenuOpen: (newOpened) =>
          setOpenedDropdown(newOpened ? rubric.id : null),
      }))}
    />
  );
};

export const ListRubricsView: React.FC<ListRubricsViewProps> = (props) => {
  const {
    version,
    canManageRubrics,
    list,
    currentTab,
    onTabClick,
    archivingRubric,
    unarchivingRubric,
    deletingRubric,
    publishingRubric,
    unpublishingRubric,
    archiveRubric,
    unarchiveRubric,
    deleteRubric,
    publishRubric,
    unpublishRubric,
    pendingAction,
    setPendingAction,
    toasts,
    dismissToast,
  } = props;

  const language = useLanguage();
  const [openedDropdown, setOpenedDropdown] = useState<string | null>(null);

  const pendingRubric = useMemo(() => {
    if (!pendingAction) {
      return null;
    }
    return list.find((rubric) => rubric.id === pendingAction?.rubricId) ?? null;
  }, [list, pendingAction]);

  const isPendingActionSubmitting = useMemo(() => {
    if (!pendingAction) {
      return false;
    }

    const rubricId = pendingAction.rubricId;

    switch (pendingAction.action) {
      case 'archive':
        return archivingRubric === rubricId;
      case 'unarchive':
        return unarchivingRubric === rubricId;
      case 'delete':
        return deletingRubric === rubricId;
      case 'publish':
        return publishingRubric === rubricId;
      case 'unpublish':
        return unpublishingRubric === rubricId;
    }
  }, [
    archivingRubric,
    deletingRubric,
    pendingAction,
    publishingRubric,
    unarchivingRubric,
    unpublishingRubric,
  ]);

  const showArchivedTab = list.some((i) => i.status === 'archived');
  const showDraftTab = list.some((i) => i.status === 'draft');
  const showLiveTab = list.some((i) => i.status === 'published');
  const showResolvedList = version === 'loaded' || version === 'empty';

  const visibleRubrics = useMemo(() => {
    return list.filter((rubric) =>
      shouldShowRubricInTab({
        status: rubric.status,
        currentTab,
      }),
    );
  }, [currentTab, list]);

  useEffect(() => {
    if (
      pendingAction &&
      !isPendingActionSubmitting &&
      !list.some((rubric) => rubric.id === pendingAction?.rubricId)
    ) {
      setPendingAction(null);
    }
  }, [isPendingActionSubmitting, list, pendingAction, setPendingAction]);

  useEffect(() => {
    if (currentTab === 'archived' && !showArchivedTab) {
      onTabClick('all');
    }

    if (currentTab === 'draft' && !showDraftTab) {
      onTabClick('all');
    }

    if (currentTab === 'published' && !showLiveTab) {
      onTabClick('all');
    }
  }, [currentTab, onTabClick, showArchivedTab, showDraftTab, showLiveTab]);

  return (
    <>
      {pendingAction && pendingRubric && (
        <RubricActionConfirmationModal
          name={pendingRubric.name}
          action={pendingAction.action}
          pending={isPendingActionSubmitting}
          onCancel={() => {
            if (!isPendingActionSubmitting) {
              setPendingAction(null);
            }
          }}
          onConfirm={() => {
            const rubricId = pendingAction?.rubricId;
            const action = pendingAction?.action;

            if (!rubricId || !action) {
              return;
            }

            if (action === 'archive') {
              archiveRubric(rubricId);
            } else if (action === 'unarchive') {
              unarchiveRubric(rubricId);
            } else if (action === 'delete') {
              deleteRubric(rubricId);
            } else if (action === 'publish') {
              publishRubric(rubricId);
            } else if (action === 'unpublish') {
              unpublishRubric(rubricId);
            }
          }}
        />
      )}

      <RubricToastStack toasts={toasts} onDismiss={dismissToast} />

      <Spacing size="md" direction="vertical">
        <S2Typography
          variant="headline-3"
          translatedContent={language.listRubricsPageTitle()}
        />

        {canManageRubrics && (
          <RubricListHeaderCards
            interviewRubricHref="/rubrics/create/interview"
            resumeRubricHref="/rubrics/create/resume"
            writtenResponseRubricHref="/rubrics/create/questions"
          />
        )}

        <RubricListTabs
          currentTab={currentTab}
          showLiveTab={showLiveTab}
          showDraftTab={showDraftTab}
          showArchivedTab={showArchivedTab}
          onAllTabClick={() => onTabClick('all')}
          onLiveTabClick={() => onTabClick('published')}
          onDraftTabClick={() => onTabClick('draft')}
          onArchivedTabClick={() => onTabClick('archived')}
        />

        {version === 'error' && <RubricListErrorState />}
        {version === 'loading' && <RubricListLoadingState />}

        {showResolvedList && (
          <RubricListLoadedState
            visibleRubrics={visibleRubrics}
            canManageRubrics={canManageRubrics}
            archivingRubric={archivingRubric}
            unarchivingRubric={unarchivingRubric}
            deletingRubric={deletingRubric}
            publishingRubric={publishingRubric}
            unpublishingRubric={unpublishingRubric}
            setPendingAction={setPendingAction}
            setOpenedDropdown={setOpenedDropdown}
            openedDropdown={openedDropdown}
          />
        )}
      </Spacing>
    </>
  );
};
